import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isInitialQueryLoading } from '../../../lib/query/listQuery';
import { usePagedListUrlState } from '../../../lib/query/usePagedListUrlState';
import { useUrlListFilter, serializeValue } from '../../../lib/query/useUrlListFilter';
import { Button, Dropdown, Space, Typography } from 'antd';
import { toast } from '../../../lib/toast';
import { DownOutlined, DownloadOutlined, FileExcelOutlined, FilePdfOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import DataTable from '../../../components/DataTable';
import { ListPage, buildCogActionsColumn } from '../../../components/list';
import ContactFilterBar, {
  type ContactFilterValues,
} from './ContactFilterBar';
import ContactViewDrawer from './ContactViewDrawer';
import ContactFormDrawer from './ContactFormDrawer';
import BulkFollowUpDrawer from './BulkFollowUpDrawer';
import ImportContactModal from './ImportContactModal';
import { downloadImportTemplate } from '../api/importContacts';

import { useContacts } from '../hooks/useContacts';
import { useContact } from '../hooks/useContact';
import { useUserOptions } from '../../../hooks/options/useUserOptions';
import { useCreateContact } from '../hooks/useCreateContact';
import { useUpdateContact } from '../hooks/useUpdateContact';
import { useDeleteContact } from '../hooks/useDeleteContact';
import { useExportContacts } from '../hooks/useExportContacts';
import { useExportContactsPdf } from '../hooks/useExportContactsPdf';
import { useContactCategories } from '../hooks/useContactCategories';
import { useContactRoles } from '../hooks/useContactRoles';
import { usePermissions } from '../../permissions/PermissionsContext';
import { useUserSegmentScope } from '../../auth/hooks/useUserSegmentScope';
import { useCustomerSegmentOptions } from '../../../hooks/options/useCustomerSegmentOptions';
import { CONTACT_FILTER_FIELDS } from '../../../routes/filterMaps';
import type { ContactListItem, ContactFormValues, ExportContactsParams } from '../types';
import {
  contactTypeLabel,
  contactTypeListSubtitle,
  contactTypeListTitle,
  normalizeContactType,
} from '../contactType';
import type { Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { getBulkFollowUpCount } from '../api/contacts';

function formatPhoneForTel(phone: string): string {
  const cleaned = phone.trim().replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return `+${cleaned.slice(1).replace(/\+/g, '')}`;
  return cleaned.replace(/\+/g, '');
}

function copyToClipboard(text: string) {
  const value = String(text ?? '');

  const execCommandCopy = () => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.width = '1px';
      textarea.style.height = '1px';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  };

  const writeText = navigator.clipboard?.writeText;
  if (writeText != null) {
    void writeText(value)
      .then(() => {
        toast.success('Copied!');
      })
      .catch(() => {
        const ok = execCommandCopy();
        (ok ? toast.success : toast.error)(ok ? 'Copied!' : 'Copy failed');
      });
    return;
  }

  const ok = execCommandCopy();
  (ok ? toast.success : toast.error)(ok ? 'Copied!' : 'Copy failed');
}

function formatDate(s: string | null | undefined): string {
  if (s == null || s === '') return '—';
  try {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
  } catch {
    return '—';
  }
}

const defaultFilter: ContactFilterValues = {
  search: undefined,
  phone: undefined,
  categoryId: undefined,
  contactType: undefined,
  role: undefined,
  ownerUserId: undefined,
  createdRange: undefined,
  segmentIds: undefined,
  contactCategoryIds: undefined,
  lastActivity: undefined,
  createdFrom: undefined,
  createdTo: undefined,
};

const ContactsPage: React.FC = () => {
  const { page, pageSize, setPage, setPageSize } = usePagedListUrlState(10);
  const [filter] = useUrlListFilter(defaultFilter, CONTACT_FILTER_FIELDS);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState<'add' | 'edit'>('add');
  const [formContactId, setFormContactId] = useState<string | null>(null);
  const [bulkSidebarOpen, setBulkSidebarOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const createdFrom = filter.createdRange?.[0]
    ? (filter.createdRange[0] as Dayjs).format?.('YYYY-MM-DD') ?? undefined
    : undefined;
  const createdTo = filter.createdRange?.[1]
    ? (filter.createdRange[1] as Dayjs).format?.('YYYY-MM-DD') ?? undefined
    : undefined;

  const listParams = useMemo(
    () => ({
      pageNumber: page,
      pageSize,
      search: filter.search || undefined,
      phone: filter.phone || undefined,
      categoryId: filter.categoryId,
      contactType: filter.contactType,
      role: filter.role,
      ownerUserId: filter.ownerUserId || undefined,
      createdFrom: filter.createdFrom ?? createdFrom,
      createdTo: filter.createdTo ?? createdTo,
      segmentIds: filter.segmentIds,
      contactCategoryIds: filter.contactCategoryIds,
      lastActivity: filter.lastActivity || undefined,
    }),
    [page, pageSize, filter.search, filter.phone, filter.categoryId, filter.contactType, filter.role, filter.ownerUserId, filter.createdFrom, filter.createdTo, filter.segmentIds, filter.contactCategoryIds, filter.lastActivity, createdFrom, createdTo]
  );

  const { data: paged, isLoading } = useContacts(listParams);
  const loadingList = isInitialQueryLoading(isLoading, paged);
  const { data: contactDetail, isLoading: loadingContact } = useContact(
    formMode === 'edit' ? formContactId : null
  );
  const viewContact = useContact(selectedContactId);
  const { data: users = [], isLoading: loadingUsers } = useUserOptions();
  const { data: categoriesData, isLoading: loadingCategories } = useContactCategories();
  const { data: rolesData, isLoading: loadingRoles } = useContactRoles();
  const categories = categoriesData ?? [];
  const roleOptions = rolesData ?? [];
  const { isScoped, assignedSegmentIds } = useUserSegmentScope();
  const { data: segments = [] } = useCustomerSegmentOptions({ activeOnly: true });
  const segmentFilterOptions = useMemo(() => {
    if (!isScoped) {
      return segments.map((s) => ({ value: s.id, label: s.label }));
    }
    const allowed = new Set(assignedSegmentIds);
    return segments.filter((s) => allowed.has(s.id)).map((s) => ({ value: s.id, label: s.label }));
  }, [isScoped, assignedSegmentIds, segments]);
  const contactCategoryOptions = useMemo(
    () => categories.filter((c) => c.isActive && c.id > 0).map((c) => ({ value: c.id, label: c.label })),
    [categories],
  );
  const deleteMutation = useDeleteContact();
  const exportMutation = useExportContacts();
  const pdfExportMutation = useExportContactsPdf();
  const { hasPermission } = usePermissions();

  // Helper to convert ContactFilterValues to the filter criteria shape for the API
  const filterCriteria = useMemo(() => ({
    search: filter.search || undefined,
    phone: filter.phone || undefined,
    categoryId: filter.categoryId,
    contactType: filter.contactType !== undefined ? Number(filter.contactType) : undefined,
    role: filter.role !== undefined ? Number(filter.role) : undefined,
    ownerUserId: filter.ownerUserId || undefined,
    createdFrom: filter.createdFrom ?? createdFrom,
    createdTo: filter.createdTo ?? createdTo,
    segmentIds: filter.segmentIds?.length ? filter.segmentIds : undefined,
    contactCategoryIds: filter.contactCategoryIds?.length ? filter.contactCategoryIds : undefined,
    lastActivity: filter.lastActivity || undefined,
  }), [filter.search, filter.phone, filter.categoryId, filter.contactType, filter.role, filter.ownerUserId, filter.createdFrom, filter.createdTo, filter.segmentIds, filter.contactCategoryIds, filter.lastActivity, createdFrom, createdTo]);

  const { data: countData, isLoading: countLoading } = useQuery({
    queryKey: ['bulk-follow-up-count', filterCriteria],
    queryFn: () => getBulkFollowUpCount(filterCriteria),
    enabled: bulkSidebarOpen,
  });

  const createMutation = useCreateContact(() => {
    setFormDrawerOpen(false);
  });
  const updateMutation = useUpdateContact(formContactId, () => {
    setFormDrawerOpen(false);
  });

  const ownerOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: u.label,
      })),
    [users]
  );

  const categoryOptions = useMemo(
    () =>
      categories
        .filter((c) => c.isActive && c.id > 0)
        .map((c) => ({
          value: c.id,
          label: c.label,
        })),
    [categories]
  );

  const handleEdit = useCallback((id: string) => {
    setFormMode('edit');
    setFormContactId(id);
    setFormDrawerOpen(true);
  }, []);

  const displayPage = paged?.pageNumber ?? page;
  const displayPageSize = paged?.pageSize ?? pageSize;

  const columns: ColumnsType<ContactListItem> = useMemo(
    () => [
      {
        title: 'SN',
        key: 'sn',
        width: 56,
        fixed: 'left',
        align: 'center',
        render: (_, __, index) => (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {(displayPage - 1) * displayPageSize + index + 1}
          </Typography.Text>
        ),
      },
      {
        title: 'Name',
        key: 'name',
        render: (_v, record) => record.displayName ?? '—',
      },
      {
        title: 'Company',
        key: 'companyName',
        render: (_v, record) => record.companyName ?? '—',
      },

      {
        title: 'Category',
        dataIndex: 'categoryLabel',
        key: 'categoryLabel',
        render: (v: string | null) => v ?? '',
      },
      {
        title: 'Type',
        dataIndex: 'contactType',
        key: 'contactType',
        render: (_value, record) => contactTypeLabel(record.contactType),
      },
      {
        title: 'Owner',
        dataIndex: 'ownerDisplayName',
        key: 'ownerDisplayName',
        render: (v: string | null) => v ?? '—',
      },
      {
        title: 'Role',
        dataIndex: 'role',
        key: 'role',
        render: (v: number | null) => {
          const option = v != null ? roleOptions.find((r) => r.value === v) : null;
          return option ? <Typography.Text type="success">{option.label}</Typography.Text> : '—';
        },
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        key: 'phone',
        render: (v: string | null, record) =>
          v ? (
            <Dropdown
              trigger={['click']}
              menu={{
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation();

                  if (!record.phone) return;

                  const normalizedPhone = formatPhoneForTel(record.phone);

                  if (key === 'copy') {
                    copyToClipboard(record.phone);
                    return;
                  }

                  if (key === 'call') {
                    window.open(`tel:${normalizedPhone}`, '_self');
                  }
                },
                items: [
                  { key: 'copy', label: 'Copy' },
                  { key: 'call', label: 'Call' },
                ] as MenuProps['items'],
              }}
            >
              <Button
                type="link"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {v}
              </Button>
            </Dropdown>
          ) : (
            ''
          ),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        render: (v: string | null, record) =>
          v ? (
            <Dropdown
              trigger={['click']}
              menu={{
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation();

                  const email = record.email;
                  if (!email) {
                    return;
                  }

                  if (key === 'search') {
                    const url = `https://mail.google.com/mail/u/0/#search/${encodeURIComponent(email)}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                    return;
                  }

                  if (key === 'copy') {
                    copyToClipboard(email);
                    return;
                  }

                  if (key === 'send') {
                    const url = `https://mail.google.com/mail/u/0/?view=cm&fs=1&to=${encodeURIComponent(
                      email
                    )}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }
                },
                items: [
                  { key: 'search', label: 'Search' },
                  { key: 'copy', label: 'Copy' },
                  { key: 'send', label: 'Send' },
                ] as MenuProps['items'],
              }}
            >
              <Button
                type="link"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {v}
              </Button>
            </Dropdown>
          ) : (
            ''
          ),
      },
      {
        title: 'WhatsApp',
        dataIndex: 'whatsAppNumber',
        key: 'whatsAppNumber',
        render: (v: string | null) =>
          v ? (
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const normalized = v.replace(/[^0-9]/g, '');
                if (!normalized) return;
                const url = `https://wa.me/${normalized}`;
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              {v}
            </Button>
          ) : (
            ''
          ),
      },
      {
        title: 'Last Activity',
        dataIndex: 'lastActivityAtUtc',
        key: 'lastActivityAtUtc',
        render: (v: string | null) => formatDate(v),
      },
      buildCogActionsColumn<ContactListItem>({
        onView: (record) => setSelectedContactId(record.id),
        onEdit: (record) => handleEdit(record.id),
        onDelete: (record) => deleteMutation.mutate(record.id),
        deleteConfirm: () => ({
          title: 'Delete contact',
          content: 'Are you sure you want to delete this contact?',
        }),
        rowNumber: (_record, index) => (displayPage - 1) * displayPageSize + index + 1,
      }),
    ],
    [deleteMutation, handleEdit, displayPage, displayPageSize],
  );

  const handleExport = useCallback((format: 'excel' | 'pdf') => {
    const exportParams: ExportContactsParams = {
      search: filter.search || undefined,
      phone: filter.phone || undefined,
      categoryId: filter.categoryId,
      contactType: filter.contactType,
      role: filter.role,
      ownerUserId: filter.ownerUserId || undefined,
      createdFrom: filter.createdFrom ?? createdFrom,
      createdTo: filter.createdTo ?? createdTo,
      segmentIds: filter.segmentIds,
      contactCategoryIds: filter.contactCategoryIds,
      lastActivity: filter.lastActivity || undefined,
    };
    if (format === 'excel') {
      exportMutation.mutate(exportParams);
    } else {
      pdfExportMutation.mutate(exportParams);
    }
  }, [filter, createdFrom, createdTo, exportMutation, pdfExportMutation]);

  const exportMenuItems: MenuProps['items'] = [
    { key: 'excel', icon: <FileExcelOutlined />, label: 'Excel (.xlsx)' },
    { key: 'pdf', icon: <FilePdfOutlined />, label: 'PDF (.pdf)' },
  ];

  const importMenuItems: MenuProps['items'] = [
    { key: 'download-template', icon: <DownloadOutlined />, label: 'Download Template' },
    { key: 'import-modal', icon: <UploadOutlined />, label: 'Import' },
  ];

  const actionsMenuItems: MenuProps['items'] = [
    ...(hasPermission('contacts.view')
      ? [
          {
            key: 'export',
            icon: <DownloadOutlined />,
            label: 'Export',
            children: exportMenuItems,
          } as const,
        ]
      : []),
    ...(hasPermission('contacts.create')
      ? [
          {
            key: 'import',
            icon: <UploadOutlined />,
            label: 'Import',
            children: importMenuItems,
          } as const,
        ]
      : []),
  ];

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadImportTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contact-import-template.xlsx';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleActionsMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'excel') {
      handleExport('excel');
    } else if (key === 'pdf') {
      handleExport('pdf');
    } else if (key === 'download-template') {
      handleDownloadTemplate();
    } else if (key === 'import-modal') {
      setImportModalOpen(true);
    }
  };

  const handleAddContact = () => {
    setFormMode('add');
    setFormContactId(null);
    setFormDrawerOpen(true);
  };

  const handleEditFromView = () => {
    if (selectedContactId) {
      setSelectedContactId(null);
      handleEdit(selectedContactId);
    }
  };

  const handleSaveForm = (values: ContactFormValues) => {
    if (formMode === 'add') {
      createMutation.mutate(values);
    } else if (formContactId) {
      updateMutation.mutate(values);
    }
  };

  const handlePaginationChange = useCallback((newPage: number, newPageSize: number) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    } else {
      setPage(newPage);
    }
  }, [pageSize, setPage, setPageSize]);

  const [, rawSetSearchParams] = useSearchParams();

  const handleFilterChange = useCallback((next: ContactFilterValues) => {
    const prepared = { ...next };
    if (next.createdRange?.[0] && next.createdRange[1]) {
      prepared.createdFrom = (next.createdRange[0] as Dayjs).format('YYYY-MM-DD');
      prepared.createdTo = (next.createdRange[1] as Dayjs).format('YYYY-MM-DD');
    } else if (next.createdRange === null) {
      prepared.createdFrom = undefined;
      prepared.createdTo = undefined;
    }
    rawSetSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('page');
        for (const def of CONTACT_FILTER_FIELDS) {
          const val = (prepared as Record<string, unknown>)[def.field];
          const serialized = serializeValue(val, def);
          if (serialized !== undefined) {
            nextParams.set(def.param, serialized);
          } else {
            nextParams.delete(def.param);
          }
        }
        return nextParams;
      },
      { replace: true },
    );
  }, [rawSetSearchParams]);

  const handleFilterReset = useCallback(() => {
    rawSetSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('page');
        for (const def of CONTACT_FILTER_FIELDS) {
          nextParams.delete(def.param);
        }
        return nextParams;
      },
      { replace: true },
    );
  }, [rawSetSearchParams]);

  const normalizedFilterType =
    filter.contactType !== undefined ? normalizeContactType(filter.contactType) : undefined;

  const pageTitle =
    normalizedFilterType === undefined
      ? 'Contacts'
      : contactTypeListTitle(normalizedFilterType);

  const pageSubtitle =
    normalizedFilterType === undefined
      ? 'Search, filter, and manage people and organizations in your workspace.'
      : contactTypeListSubtitle(normalizedFilterType);

  return (
    <>
      <ListPage
        title={pageTitle}
        subtitle={pageSubtitle}
        actions={
          <Space>
            {(hasPermission('contacts.view') || hasPermission('contacts.create')) && (
              <Dropdown
                menu={{ items: actionsMenuItems, onClick: handleActionsMenuClick }}
                trigger={['click']}
              >
                <Button icon={<DownOutlined />}>
                  Actions
                </Button>
              </Dropdown>
            )}
            {hasPermission('tasks.create') && (
              <Button onClick={() => setBulkSidebarOpen(true)}>
                Add Follow-up
              </Button>
            )}
            {hasPermission('contacts.create') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddContact}>
                Add Contact
              </Button>
            )}
          </Space>
        }
        filters={(
          <ContactFilterBar
            value={filter}
            onChange={handleFilterChange}
            onReset={handleFilterReset}
            categoryOptions={categoryOptions}
            roleOptions={roleOptions}
            ownerOptions={ownerOptions}
            loading={loadingUsers}
            segmentOptions={segmentFilterOptions}
            contactCategoryOptions={contactCategoryOptions}
          />
        )}
      >
        <DataTable<ContactListItem>
          columns={columns}
          dataSource={paged?.items ?? []}
          loading={loadingList}
          rowKey="id"
          bordered
          striped
          scroll={{ x: 'max-content' }}
          pagination={
            paged
              ? {
                  current: paged.pageNumber,
                  pageSize: paged.pageSize,
                  total: paged.totalCount,
                  onChange: handlePaginationChange,
                }
              : undefined
          }
          emptyText="No contacts yet. Add your first contact."
        />
      </ListPage>

      <ContactViewDrawer
        open={selectedContactId != null}
        onClose={() => setSelectedContactId(null)}
        contact={viewContact.data}
        loading={viewContact.isLoading}
        onEdit={handleEditFromView}
      />

      <ContactFormDrawer
        open={formDrawerOpen}
        onClose={() => setFormDrawerOpen(false)}
        mode={formMode}
        contactId={formContactId}
        contact={contactDetail}
        loadingContact={formMode === 'edit' && formContactId ? loadingContact : false}
        ownerOptions={ownerOptions}
        categoryOptions={categoryOptions}
        roleOptions={roleOptions}
        loadingCategories={loadingCategories}
        loadingRoles={loadingRoles}
        loadingOwners={loadingUsers}
        onSave={handleSaveForm}
        saving={createMutation.isPending || updateMutation.isPending}
      />

      <BulkFollowUpDrawer
        open={bulkSidebarOpen}
        onClose={() => setBulkSidebarOpen(false)}
        filterValues={filter}
        approximateCount={countData?.approximateCount ?? 0}
        countLoading={countLoading}
        categoryOptions={categoryOptions}
      />

      <ImportContactModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
      />
    </>
  );
};

export default ContactsPage;
